/**
 * Product Media Gallery - Swiper Element Controller
 * Handles variant-to-gallery synchronization and responsive effect switching.
 *
 * Dependencies:
 * - swiper-element-bundle.min.js (loaded before this script)
 * - snippets/product-media-gallery.liquid (Swiper markup)
 */

const DESKTOP_BREAKPOINT = 750;

if (!customElements.get("product-media-gallery")) {
  customElements.define(
    "product-media-gallery",
    class ProductMediaGallery extends HTMLElement {
      constructor() {
        super();
        this.mainSwiperEl = null;
        this.thumbsSwiperEl = null;
        this.currentEffect = null;
        this.resizeHandler = null;
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.mainSwiperEl = this.querySelector(".product-media-main-swiper");
        this.thumbsSwiperEl = this.querySelector(".product-media-thumbs-swiper");

        if (this.mainSwiperEl) {
          this.initSwiper();
          this.resizeHandler = this.handleResize.bind(this);
          window.addEventListener("resize", this.resizeHandler);
        }
      }

      disconnectedCallback() {
        if (this.resizeHandler) {
          window.removeEventListener("resize", this.resizeHandler);
        }
      }

      /**
       * Determines the appropriate effect based on viewport width.
       * @returns {string} 'fade' for desktop, 'slide' for mobile
       */
      getEffectForViewport() {
        return window.innerWidth >= DESKTOP_BREAKPOINT ? "fade" : "slide";
      }

      /**
       * Initializes the main Swiper with the appropriate effect.
       */
      initSwiper() {
        const effect = this.getEffectForViewport();
        this.currentEffect = effect;

        const thumbsId = this.thumbsSwiperEl?.id;

        Object.assign(this.mainSwiperEl, {
          slidesPerView: "auto",
          spaceBetween: 20,
          loop: false,
          effect: effect,
          fadeEffect: { crossFade: true },
          thumbs: thumbsId ? { swiper: `#${thumbsId}` } : undefined,
        });

        this.mainSwiperEl.initialize();
        this.initSlideChangeHandler();

        this.classList.add("is-initialized");
      }

      /**
       * Sets up slide change event handler for video pause/play.
       */
      initSlideChangeHandler() {
        this.mainSwiperEl.addEventListener("swiperslidechange", () => {
          this.handleSlideChange();
        });
      }

      /**
       * Handles slide change - pauses videos on inactive slides, plays on active.
       */
      handleSlideChange() {
        const slides = this.mainSwiperEl.querySelectorAll("swiper-slide");
        const activeIndex = this.mainSwiperEl.swiper?.activeIndex ?? 0;

        slides.forEach((slide, index) => {
          const video = slide.querySelector("video");
          if (!video) return;

          if (index === activeIndex) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      }

      /**
       * Handles viewport resize and reinitializes if effect needs to change.
       */
      handleResize() {
        const newEffect = this.getEffectForViewport();

        if (newEffect !== this.currentEffect && this.mainSwiperEl?.swiper) {
          const currentIndex = this.mainSwiperEl.swiper.activeIndex;

          this.mainSwiperEl.swiper.destroy(true, true);

          this.currentEffect = newEffect;
          const thumbsId = this.thumbsSwiperEl?.id;

          Object.assign(this.mainSwiperEl, {
            slidesPerView: "auto",
            spaceBetween: 20,
            loop: false,
            effect: newEffect,
            fadeEffect: { crossFade: true },
            thumbs: thumbsId ? { swiper: `#${thumbsId}` } : undefined,
          });

          this.mainSwiperEl.initialize();

          if (currentIndex > 0) {
            this.mainSwiperEl.swiper.slideTo(currentIndex, 0);
          }
        }
      }

      /**
       * Navigates the gallery to show the media matching the given ID.
       * Called when variant selection changes.
       * @param {string|number} mediaId - The media ID to display
       */
      setActiveMedia(mediaId) {
        if (!this.mainSwiperEl?.swiper) {
          return;
        }

        const slides = this.mainSwiperEl.querySelectorAll("swiper-slide");
        const targetIndex = [...slides].findIndex(
          (slide) => slide.dataset.mediaId === String(mediaId)
        );

        if (targetIndex !== -1) {
          this.mainSwiperEl.swiper.slideTo(targetIndex);
        }
      }

      /**
       * Gets the currently active media ID.
       * @returns {string|null} The active media ID or null
       */
      getActiveMediaId() {
        if (!this.mainSwiperEl?.swiper) {
          return null;
        }

        const activeSlide = this.mainSwiperEl.querySelector("swiper-slide.swiper-slide-active");
        return activeSlide?.dataset.mediaId || null;
      }
    }
  );
}

/**
 * Checks Door Vision API health and shows button if product is compatible.
 * Runs once on page load.
 *
 * Note: 404 responses are expected and indicate the product is not available
 * in Door Vision. These will appear in browser console but are handled correctly.
 * The browser logs these automatically and cannot be suppressed.
 */
function checkDoorVisionAvailability() {
  const doorVisionButtons = document.querySelectorAll('[data-door-vision-button]');

  doorVisionButtons.forEach((button) => {
    const productId = button.dataset.productId;
    if (!productId) return;

    const url = `https://server.door-vision.com/api/v1/health/directdoors/${productId}`;

    fetch(url)
      .then((response) => {
        if (response.status === 200) {
          button.removeAttribute('hidden');
        }
      })
      .catch(() => {
        /**
         * Network errors - button remains hidden
         * 404s are logged by browser but are expected behavior
         */
      });
  });
}

/* Check Door Vision availability on page load */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', checkDoorVisionAvailability);
} else {
  checkDoorVisionAvailability();
}
